import { ArrowLeft, Loader2, Save } from "lucide-react";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../../components/ui/button";
import {
  queryClient,
  useCreateSkill,
  useSkill,
  useUpdateSkill,
} from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import useSkillsStore from "../../../../store/skills/skillsStore";
import { SkillStatus } from "../../../../services/skills/skillsEndpoints.interface";
import { toast } from "sonner";
import SkillMetadataForm from "./components/SkillMetadataForm";
import SkillInstructionsEditor from "./components/SkillInstructionsEditor";
import SkillTemplatesSection from "./components/SkillTemplatesSection";

const SkillEditorPage = () => {
  const { skillId } = useParams<{ skillId?: string }>();
  const workspace = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();
  const isEditMode = !!skillId;

  const editor = useSkillsStore((s) => s.editor);
  const isDirty = useSkillsStore((s) => s.isDirty);
  const resetEditor = useSkillsStore((s) => s.resetEditor);
  const loadSkillIntoEditor = useSkillsStore((s) => s.loadSkillIntoEditor);
  const setEditor = useSkillsStore((s) => s.setEditor);

  const { data: existingSkill, isLoading: loadingSkill }: any = useSkill({
    workspaceId: workspace?.id,
    skillId,
    enabled: !!workspace?.id && !!skillId,
  });

  const createMutation = useCreateSkill();
  const updateMutation = useUpdateSkill();
  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  useEffect(() => {
    if (!isEditMode) {
      resetEditor();
    }
  }, [isEditMode]);

  useEffect(() => {
    if (existingSkill && isEditMode) {
      loadSkillIntoEditor(existingSkill);
    }
  }, [existingSkill, isEditMode]);

  const handleSave = (status?: SkillStatus) => {
    if (!editor.name.trim()) {
      toast.error("Skill name is required.");
      return;
    }

    if (isEditMode && skillId) {
      updateMutation.mutate(
        {
          workspace_id: workspace?.id,
          skill_id: skillId,
          name: editor.name,
          display_name: editor.displayName,
          description: editor.description,
          kind: editor.kind,
          instructions_content: editor.instructionsContent,
          priority: editor.priority,
          keywords: editor.keywords,
          templates: editor.templates,
          status: (status || editor.status) as SkillStatus,
          visibility: editor.visibility,
          output_format: editor.structuredOutput.length > 0 ? JSON.stringify(editor.structuredOutput) : undefined,
          output_format_type: editor.structuredOutput.length > 0 ? "json_schema" : "none",
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["skills", workspace?.id],
            });
            queryClient.invalidateQueries({
              queryKey: ["skill", workspace?.id, skillId],
            });
            useSkillsStore.getState().setIsDirty(false);
            toast.success("Skill updated successfully.");
          },
          onError: () => {
            toast.error("Failed to update skill.");
          },
        },
      );
    } else {
      createMutation.mutate(
        {
          workspace_id: workspace?.id,
          name: editor.name,
          display_name: editor.displayName || editor.name,
          description: editor.description,
          kind: editor.kind,
          instructions_content: editor.instructionsContent,
          priority: editor.priority,
          keywords: editor.keywords,
          templates: editor.templates,
          visibility: editor.visibility,
          output_format: editor.structuredOutput.length > 0 ? JSON.stringify(editor.structuredOutput) : undefined,
          output_format_type: editor.structuredOutput.length > 0 ? "json_schema" : "none",
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["skills", workspace?.id],
            });
            useSkillsStore.getState().setIsDirty(false);
            toast.success("Skill created successfully.");
            navigate("..", { replace: true });
          },
          onError: () => {
            toast.error("Failed to create skill.");
          },
        },
      );
    }
  };

  if (isEditMode && loadingSkill) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("..")}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <h1 className="text-sm font-semibold text-foreground">
            {isEditMode ? "Edit Skill" : "New Skill"}
          </h1>
          {isDirty && (
            <span className="text-xs text-muted-foreground">(unsaved changes)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditMode && editor.status === SkillStatus.DRAFT && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSave(SkillStatus.ACTIVE)}
              disabled={isSaving}
            >
              Publish
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => handleSave()}
            disabled={isSaving || !isDirty}
          >
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left - Instructions + Templates */}
        <div className="flex flex-1 flex-col overflow-y-auto p-5">
          <SkillInstructionsEditor />
          <SkillTemplatesSection />
        </div>

        {/* Right sidebar - Metadata */}
        <div className="w-96 shrink-0 overflow-y-auto border-l border-border p-5">
          <SkillMetadataForm />
        </div>
      </div>
    </div>
  );
};

export default SkillEditorPage;
